import {environment} from '@environments/environment';
import {
  AuthRealm,
  AuthScheme,
  Brand,
  BusinessContext,
  IBusinessPartner,
  IBusinessRelationship,
  IOutletPeer,
  OutletPeer,
  User,
  VehicleType
} from '@service-and-repairs/awpintegrationlib';
import {EshWriteMode, UserSettings} from '../interfaces/user-settings';

export class UserData extends User {

  static copy(user: UserData | null): UserData | null {
    if (user === null) {
      return null;
    }

    // Restore integration-library prototypes after detaching all nested state.
    const copy: UserData = Object.assign(new UserData(), User.constructFromPlainObject(structuredClone(user)));
    copy.setAllOutletPeers(OutletPeer.constructFromPlainObjectList(copy.getAllOutletPeers()));
    return copy;
  }

  private newCaseAfterVehicleIdentification: boolean;
  private loginName: string;
  private settingsId: string;

  private caseTabShowCustomer: boolean;
  private caseTabShowModel: boolean;
  private caseTabShowTitle: boolean;
  private caseTabShowVin: boolean;

  private allOutletPeers: IOutletPeer[] = [];
  private eshWriteMode: EshWriteMode = 'ASK';
  private partsDmsEmployeeNumber: string;
  private partsDmsUserPassword: string;
  private requestPartLocation: boolean = false;

  getRequestPartLocation(): boolean {
    return this.requestPartLocation;
  }

  setRequestPartLocation(requestPartLocation: boolean | undefined): void {
    this.requestPartLocation = requestPartLocation ?? false;
  }

  getPartsDmsEmployeeNumber(): string {
    return this.partsDmsEmployeeNumber;
  }

  setPartsDmsEmployeeNumber(partsDmsEmployeeNumber: string): void {
    this.partsDmsEmployeeNumber = partsDmsEmployeeNumber;
  }

  getPartsDmsUserPassword(): string {
    return this.partsDmsUserPassword;
  }

  setPartsDmsUserPassword(partsDmsUserPassword: string): void {
    this.partsDmsUserPassword = partsDmsUserPassword;
  }

  getAllOutletPeers(): IOutletPeer[] {
    return this.allOutletPeers;
  }

  setAllOutletPeers(peers: IOutletPeer[]): void {
    this.allOutletPeers = peers;
  }

  getEshWriteMode(): EshWriteMode {
    return this.eshWriteMode;
  }

  setEshWriteMode(eshWriteMode: EshWriteMode): void {
    this.eshWriteMode = eshWriteMode || 'ASK';
  }

  getCaseTabShowCustomer(): boolean {
    return this.caseTabShowCustomer;
  }

  setCaseTabShowCustomer(caseTabShowCustomer: boolean): void {
    this.caseTabShowCustomer = caseTabShowCustomer;
  }

  getCaseTabShowModel(): boolean {
    return this.caseTabShowModel;
  }

  setCaseTabShowModel(caseTabShowModel: boolean): void {
    this.caseTabShowModel = caseTabShowModel;
  }

  getCaseTabShowTitle(): boolean {
    return this.caseTabShowTitle;
  }

  setCaseTabShowTitle(caseTabShowTitle: boolean): void {
    this.caseTabShowTitle = caseTabShowTitle;
  }

  getCaseTabShowVin(): boolean {
    return this.caseTabShowVin;
  }

  setCaseTabShowVin(caseTabShowVin: boolean): void {
    this.caseTabShowVin = caseTabShowVin;
  }

  getServiceCaseTabLineCount(): number {
    return [
      this.getCaseTabShowCustomer(),
      this.getCaseTabShowModel(),
      this.getCaseTabShowTitle(),
      this.getCaseTabShowVin()
    ].filter(x => x).length;
  }

  getLoginName(): string {
    return this.loginName;
  }

  setLoginName(loginName: string): void {
    this.loginName = loginName;
  }

  getSettingsId(): string {
    return this.settingsId;
  }

  setSettingsId(id: string): void {
    this.settingsId = id;
  }

  getNewCaseAfterVehicleIdentification(): boolean {
    return this.newCaseAfterVehicleIdentification;
  }

  setNewCaseAfterVehicleIdentification(value: boolean): void {
    this.newCaseAfterVehicleIdentification = value;
  }

  setSelectedBusiness(businessPartnerId: string, vehicleType: string): void {
    const selectedPartner = this.findSelectedBusinessPartner(businessPartnerId);
    this.setBusinessPartner(selectedPartner);

    // If we had to fall back to a different partner, we might have to fall back to a different vehicle type as well
    const relationship = this.findBusinessRelationship(vehicleType);
    this.setSelectedVehicleType(relationship?.getVehicleType());
  }

  getAuthScheme(): AuthScheme {
    const context = this.getContext();
    switch (context) {
      case BusinessContext.B2D:
        return AuthScheme.B2D;
      case BusinessContext.B2D_INTERNAL:
        return AuthScheme.B2D;
      default:
        throw new Error(`[Webapp] [User] Could not determine auth scheme for unknown business context "${context}".`);
    }
  }

  getAuthRealm(): AuthRealm {
    if (environment.alphaRealmEnabled === true) {
      return AuthRealm.alpha;
    }
    return this.getContext() === BusinessContext.B2D ? AuthRealm.internetb2x : AuthRealm.intranetb2x;
  }

  /**
   * Determines a business number, that matches a given brand. If there is no business number for that brand or no
   * brand
   * was specified, a brand matching the selected vehicle type will be determined.
   * @param brand The brand of the vehicle from the active service case. If undefined, the business number matching
   *   the selected vehicle type will be used.
   */
  getBusinessNumber(brand: string): string {
    let buNo: string = this.findBuNoForBrand(brand);

    if (!buNo) {
      buNo = this.getSelectedVehicleType() === VehicleType.MOTORCYCLE
        ? this.getMotorcycleBuNo()
        : this.getPassengerCarBuNo();
    }

    // If any attempt of educated guessing failed to determine a BuNo, use the first we can find.
    return buNo ?? this.getBusinessPartner()?.getBusinessRelationships().at(0).getBusinessNumber();
  }

  getBrandsOfSelectedBusinessPartner(): Brand[] {
    return this.getBusinessPartner()
      ?.getBusinessRelationships()
      .filter(br => br.getVehicleType() === this.getSelectedVehicleType())
      .map(br => br.getBrands())
      .flat()
      .filter((brand, index, brands) => brands.indexOf(brand) === index) ?? [];
  }

  hasValidBusinessRelationship(): boolean {
    return this.getBusinessPartners()?.some(bp => bp?.getBusinessRelationships()?.some(br => !!br));
  }

  setUserSettings(settings: UserSettings): void {
    this.setDefaultApp(settings.defaultApp);
    this.setKeyReaderId(settings.keyReaderId);
    this.setNewCaseAfterVehicleIdentification(settings.newCaseAfterVehicleIdentification);
    this.setLocale(settings.systemLanguage);

    this.setMileageUnit(settings.mileageUnit);
    this.setTemperatureUnit(settings.temperatureUnit);
    this.setVolumeUnit(settings.volumeUnit);
    this.setPressureUnit(settings.pressureUnit);
    this.setTreadDepthUnit(settings.treadDepthUnit);

    this.setCaseTabShowCustomer(settings.caseTabShowCustomer);
    this.setCaseTabShowModel(settings.caseTabShowModel);
    this.setCaseTabShowTitle(settings.caseTabShowTitle);
    this.setCaseTabShowVin(settings.caseTabShowVin);

    this.setAddedQuantityStrategy(settings.addedQuantityStrategy);
    this.setDefaultPartsSearchBrand(settings.defaultPartsSearchBrand);
    this.setShowPicturePanelTreeSearch(settings.showPicturePanelTreeSearch);
    this.setShowPartNumbers(settings.showPartNumbers);
    this.setShowRetrofittableParts(settings.showRetrofittableParts);
    this.setShowOmittedParts(settings.showOmittedParts);
    this.setAutoExpandExpandableSections(settings.autoExpandExpandableSections);
    this.setPartsDmsEmployeeNumber(settings.partsDmsEmployeeNumber);
    this.setPartsDmsUserPassword(settings.partsDmsUserPassword);
    this.setRequestPartLocation(settings.requestPartLocation);

    this.setDmsPassword(settings.dmsUserPassword);
    this.setDmsUsername(settings.dmsUserId);
    this.setDmsServiceAdvisorId(settings.dmsServiceAdvisorId);
    this.setEshWriteMode(settings.eshWriteMode);
    this.setSelectedBusiness(settings.selectedBusinessPartnerId, settings.selectedVehicleType);
    this.setSelectedVehicleType(this.vehicleTypeFromString(settings.selectedVehicleType));
  }

  private vehicleTypeFromString(value: string): VehicleType {
    let vehicleType = VehicleType.UNKNOWN;
    if (value === VehicleType.PASSENGER_CAR) {
      vehicleType = VehicleType.PASSENGER_CAR;
    } else if (value === VehicleType.MOTORCYCLE) {
      vehicleType = VehicleType.MOTORCYCLE;
    }
    return vehicleType;
  }

  private findSelectedBusinessPartner(businessPartnerId: string): IBusinessPartner {
    // find the business partner with the given id or any business partner with valid business relationships
    const selectedBusinessPartner = this.getBusinessPartners()
      .find(bp => bp?.getBusinessPartnerId() && bp?.getBusinessPartnerId() === businessPartnerId);
    return selectedBusinessPartner || this.getBusinessPartners()
      .find(bp => bp?.getBusinessRelationships().some(br => !!br));
  }

  private findBusinessRelationship(vehicleType: string): IBusinessRelationship {
    // find the business relationship with the given vehicle type or any business relationship with a valid vehicle type
    const selectedBusinessRelationship: IBusinessRelationship = this.getBusinessPartner()?.getBusinessRelationships()
      ?.find((br: IBusinessRelationship): boolean => br?.getVehicleType() && br?.getVehicleType() === vehicleType);
    return selectedBusinessRelationship || this.getBusinessPartner()?.getBusinessRelationships()
      ?.find((br: IBusinessRelationship) => br?.getVehicleType());
  }

  private getMotorcycleBuNo(): string {
    if (this.findBuNoForBrand(Brand.MOT)) {
      return this.findBuNoForBrand(Brand.MOT);
    }
    return this.findFirstBuNoForVehicleType(VehicleType.MOTORCYCLE);
  }

  private getPassengerCarBuNo(): string {
    const orderedPassengerCarBrands: Brand[] = [Brand.BMW, Brand.MINI, Brand.BMW_i, Brand.RR, Brand.ZINORO];
    for (let brand of orderedPassengerCarBrands) {
      if (this.findBuNoForBrand(brand)) {
        return this.findBuNoForBrand(brand);
      }
    }
    return this.findFirstBuNoForVehicleType(VehicleType.PASSENGER_CAR);
  }

  private findBuNoForBrand(brand: string): string {
    const businessPartner: IBusinessPartner = this.getBusinessPartner();
    const relationships: IBusinessRelationship[] = businessPartner?.getBusinessRelationships();
    let buNos: string[] = relationships
      ?.filter((r: IBusinessRelationship): boolean => r.getBrands()?.some(((b: Brand): boolean => b === brand)))
      .map((r: IBusinessRelationship): string => r.getBusinessNumber()) || [];

    // HST legacy BuNo handling: Some dealers have multiple active BuNos for the same vehicle type and brand.
    // Only one of these BuNos is marked as "default", the other ones should be disregarded and HST is working
    // on removing these non-default BuNos from the HST database. Until then, we have to deal with these incorrect
    // legacy BuNos. However, HST does not provide the "default" flag through its library, so no automatic filtering is
    // possible. The following is a hardcoded list of well-known "non-default" BuNos that we should disregard.
    const dpNo: string = businessPartner?.getDistributionPartnerNumber();
    const outletNo: string = businessPartner?.getOutletNumber();
    if (dpNo === '04612' && outletNo === '01' && brand === Brand.BMW) {
      buNos = buNos.filter((buNo: string): boolean => buNo !== '20993');
    }

    return buNos[0];
  }

  private findFirstBuNoForVehicleType(vehicleType: VehicleType): string {
    return this.findBusinessRelationship(vehicleType)?.getBusinessNumber();
  }
}
